import { UiSchema, getUiOptions, WidgetProps } from "@rjsf/utils";
import { JSONSchema7 } from "json-schema";

function uiOption<T = string>(uiSchema: UiSchema | undefined, option: string) {
  return (
    uiSchema && ((getUiOptions(uiSchema)![option] as unknown) as T | undefined)
  );
}

/** Extracts placeholder text from the [UI schema](https://react-jsonschema-form.readthedocs.io/en/latest/api-reference/uiSchema/#placeholder). */
export const placeholder = (props: { uiSchema?: UiSchema }) =>
  uiOption(props.uiSchema, "placeholder");

/** Extracts widget from the [UI schema](https://react-jsonschema-form.readthedocs.io/en/latest/advanced-customization/custom-widgets-fields/). */
export const widget = (props: { uiSchema?: UiSchema }) =>
  uiOption(props.uiSchema, "widget");

/** Extracts title from the [UI schema](https://react-jsonschema-form.readthedocs.io/en/latest/api-reference/uiSchema/#title) or props. */
export const title = (props: {
  schema: JSONSchema7;
  title?: string;
  uiSchema?: UiSchema;
}) => uiOption(props.uiSchema, "title") || props.title || props.schema.title;

/** Extracts description from the [UI schema](https://react-jsonschema-form.readthedocs.io/en/latest/api-reference/uiSchema/#description) or JSON schema. */
export const description = (props: {
  schema: JSONSchema7;
  uiSchema?: UiSchema;
}) => uiOption(props.uiSchema, "description") || props.schema.description;

/** Extracts label from the [UI schema](https://react-jsonschema-form.readthedocs.io/en/latest/api-reference/uiSchema/#label) or JSON schema. */
export const label = (props: { label?: string; uiSchema?: UiSchema }) =>
  uiOption(props.uiSchema, "label") || props.label;

/** Custom UI option to label array items in Attribute Editor. */
export const itemLabel = (props: { uiSchema?: UiSchema }) =>
  uiOption(props.uiSchema, "itemLabel");

/** Custom UI option to wrap in a container. */
export const shouldUseContainer = (props: { uiSchema?: UiSchema }): boolean => {
  const value = uiOption<boolean | string>(props.uiSchema, "container");
  return value === true || value === "true";
};

/** [Enumerated values](https://react-jsonschema-form.readthedocs.io/en/latest/usage/single/#enumerated-values). */
type EnumOption = {
  /** The actual enum value. */
  value: string;
  /** The human-friendly label. JSRF sets this to the value if not customized. */
  label: string;
};

/** Extracts enum options. */
export const enums = (props: WidgetProps) => {
  const enumOptions = props.options.enumOptions as EnumOption[];
  const enumDisabled = props.options.enumDisabled as string[] | undefined; // matches value
  return { enumOptions, enumDisabled, emptyValue: props.options.emptyValue };
};

/** Utility for selecting values with `uniqueItems` which requires us to preserver the enum order. */
export function selectUniqueItemsValue<T>(value: T, at: number, selected: T[]) {
  if (selected.length === 0) {
    return [value];
  }
  return selected.slice(0, at).concat(value, selected.slice(at));
}

/** Utility for deselecting values with `uniqueItems` which requires us to preserver the enum order. */
export function deselectUniqueItemsValue<T>(value: T, selected: T[]) {
  return selected.filter(v => v !== value);
}
